import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import { Check, X } from 'lucide-react';

const Exosomes = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHelmet 
        title="MCT Exosomes - Autologous Regenerative Therapy"
        description="Discover how MCT System delivers autologous exosomes for medical use through photothermal biomodulation, yielding significant regenerative outcomes."
        keywords="MCT exosomes, autologous exosomes, exosome therapy, regenerative medicine, photothermal biomodulation, cellular messengers"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-white via-teal-50/20 to-white">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-light mb-8 text-gray-900 animate-fade-in" style={{ fontFamily: 'Raleway, sans-serif' }}>
            MCT <span className="text-teal-500 font-normal">Exosomes</span>
          </h1>
          <p className="text-2xl md:text-3xl text-gray-700 font-light max-w-4xl mx-auto mb-6 leading-relaxed animate-fade-in">
            The possibility of obtaining exosomes and other EVs signals a new era in increasing the power of regenerative medicine treatments
          </p>
          <p className="text-xl text-gray-600 font-light max-w-4xl mx-auto animate-fade-in">
            The MCT System goes one step beyond cells by obtaining autologous injectable exosomes for medical use, yielding significant outcomes
          </p>
        </div>
      </section>

      {/* Three Benefits Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Growth Factors */}
            <div className="bg-gradient-to-br from-teal-50 to-white p-10 rounded-3xl shadow-soft hover:shadow-xl transition-all duration-500">
              <div className="text-center mb-6">
                <div className="text-5xl font-light text-teal-500 mb-4">70%</div>
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4 text-center">Growth Factors</h3>
              <p className="text-gray-600 font-light text-center leading-relaxed">
                Photothermal biostimulation elicits controlled and sustained release of growth factors from platelets, achieving a more physiological effect
              </p>
            </div>

            {/* Autologous Exosomes */}
            <div className="bg-gradient-to-br from-teal-50 to-white p-10 rounded-3xl shadow-soft hover:shadow-xl transition-all duration-500">
              <div className="text-center mb-6">
                <svg className="w-20 h-20 mx-auto text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4 text-center">Autologous Exosomes</h3>
              <p className="text-gray-600 font-light text-center leading-relaxed">
                Cell and platelet photothermal conditioning stimulates exosome release, resulting in an enhanced regenerative product
              </p>
            </div>

            {/* ATP Synthesis */}
            <div className="bg-gradient-to-br from-teal-50 to-white p-10 rounded-3xl shadow-soft hover:shadow-xl transition-all duration-500">
              <div className="text-center mb-6">
                <svg className="w-20 h-20 mx-auto text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-light text-gray-900 mb-4 text-center">ATP Synthesis</h3>
              <p className="text-gray-600 font-light text-center leading-relaxed">
                Photothermal biomodulation stimulates cellular metabolism and enhances ATP synthesis
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Autologous vs Non-Autologous Comparison */}
      <section className="py-32 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6" style={{ fontFamily: 'Raleway, sans-serif' }}>
              MCT System delivers <span className="text-teal-500 font-normal">Autologous Exosomes</span>
            </h2>
            <p className="text-xl text-gray-600 font-light">
              The natural and compatible messengers of your own body
            </p>
          </div>

          {/* Comparison Table */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Autologous */}
            <div className="bg-white p-10 rounded-3xl shadow-xl border-2 border-teal-200">
              <div className="text-center mb-8">
                <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/xvvebh9z_autologous.jpg"
                    alt="MCT Device - Autologous System"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-3xl font-light text-gray-900">Autologous</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-gray-600 font-light">Source:</span>
                  <span className="text-gray-900 font-medium">Same patient cells</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-gray-600 font-light">Level:</span>
                  <span className="text-teal-600 font-medium">Medical</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-gray-600 font-light">Certification:</span>
                  <span className="text-teal-600 font-medium">Medsafe</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-gray-600 font-light">Use:</span>
                  <span className="text-teal-600 font-medium">Injectable</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-gray-600 font-light">Content:</span>
                  <span className="text-teal-600 font-medium">Reliable</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-light">Results:</span>
                  <span className="text-teal-600 font-medium">Significant</span>
                </div>
              </div>
            </div>

            {/* Non-Autologous */}
            <div className="bg-white p-10 rounded-3xl shadow-xl border-2 border-gray-200">
              <div className="text-center mb-8">
                <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-2xl flex items-center justify-center p-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/foeoscbh_image.png"
                    alt="Vial - Non-autologous Products"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-3xl font-light text-gray-900">Non-autologous</h3>
              </div>
              
              <div className="space-y-4 opacity-60">
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-gray-600 font-light">Source:</span>
                  <span className="text-gray-700 font-medium">Animal, Plants</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-gray-600 font-light">Level:</span>
                  <span className="text-gray-700 font-medium">Cosmetic</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-gray-600 font-light">Certification:</span>
                  <span className="text-gray-700 font-medium">Cosmetic</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-gray-600 font-light">Use:</span>
                  <span className="text-gray-700 font-medium">Topical</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                  <span className="text-gray-600 font-light">Content:</span>
                  <span className="text-gray-700 font-medium">Uncertain</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-light">Results:</span>
                  <span className="text-gray-700 font-medium">Uncertain</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blue Light & Temperature Science */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Blue Light */}
            <div className="space-y-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-500 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-3xl font-light text-gray-900">EXOSOMES and Blue Light</h3>
              <div className="space-y-4 text-gray-600 font-light leading-relaxed">
                <p>Blue light stimulus improves therapeutic effect.</p>
                <p>Blue light exposure elevates miRNAs levels and increases proangiogenic capabilities of MSCs.</p>
                <p>Blue opsin photo-transduction is an invaluable tool for cell regenerative power enhancement.</p>
              </div>
            </div>

            {/* Temperature */}
            <div className="space-y-6">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-500 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-3xl font-light text-gray-900">EXOSOMES and Temperature</h3>
              <div className="space-y-4 text-gray-600 font-light leading-relaxed">
                <p>Temperature increases the frequency of exosome-cell membrane fusion events.</p>
                <p>Exosomes release kinetics are faster at higher temperatures.</p>
                <p>Temperature reduces the number of tethered exosomes.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exo-Tech Section */}
      <section className="py-32 px-6 bg-gradient-to-br from-teal-50/50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Image */}
            <div className="relative animate-slide-in-left">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/wz4tzzhg_image.png"
                  alt="Exo-Tech Bioimplant - Designed to Work Together"
                  className="w-full h-[600px] object-contain bg-gradient-to-br from-gray-50 to-white p-8"
                />
              </div>
            </div>

            {/* Right: Content */}
            <div className="space-y-8 animate-slide-in-right">
              <div className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                Add-on for MCT Exosome Protocols
              </div>
              
              <h2 className="text-4xl md:text-5xl font-light text-gray-900" style={{ fontFamily: 'Raleway, sans-serif' }}>
                <span className="text-teal-500 font-normal">Exo-Tech:</span> The First Exosome Bioimplant
              </h2>
              
              <div className="space-y-6 text-lg text-gray-600 font-light leading-relaxed">
                <p>
                  <strong className="text-gray-900">Exo-Tech</strong> transforms the patient's plasma into an <strong className="text-gray-900">injectable, viscoelastic scaffold</strong> enriched with <strong className="text-gray-900">MCT exosomes</strong> prepared using the <strong className="text-gray-900">MCT System</strong>.
                </p>
                
                <p>
                  The exosome bioimplant delivers mechanical support and sustained regenerative signaling, prolonging bioactivity and enhancing skin quality.
                </p>
                
                <p>
                  <strong className="text-gray-900">Together, MCT System and Exo-Tech</strong> provide a complete workflow—from <strong className="text-gray-900">MCT exosome preparation</strong> to a <strong className="text-gray-900">next-generation injectable bioimplant</strong>—with <strong className="text-gray-900">localized retention</strong> and <strong className="text-gray-900">natural, long-lasting outcomes</strong>.
                </p>
              </div>

              <div className="pt-4">
                <a
                  href="https://customer-assets.emergentagent.com/job_celltech-showcase/artifacts/cro7cm5m_dossier%20MCT%20Exo-Tech%202026_digital.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-teal-400 to-teal-500 hover:from-teal-500 hover:to-teal-600 text-white px-10 py-5 rounded-2xl font-medium transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.02]"
                >
                  Discover Exo-Tech
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Exosomes;
